import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { usersAPI } from '../lib/api';
import { toast } from 'sonner';
import { STATUS } from '../lib/theme';
import {
    Users,
    UserPlus,
    Edit2,
    Trash2,
    Key,
    Shield,
    User as UserIcon,
    Save
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';

export function UserManagementPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const { user: currentUser } = useAuth();

    // Form states
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        role: 'user'
    });
    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await usersAPI.getAll();
            setUsers(response.data.users);
        } catch (error) {
            toast.error('Gagal memuat daftar user');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async () => {
        if (!formData.username || !formData.password) {
            toast.error('Username dan password harus diisi');
            return;
        }

        try {
            const response = await usersAPI.create(formData.username, formData.password, formData.role);
            if (response.data.success) {
                toast.success(
                    <div className="flex flex-col gap-1">
                        <div className="font-semibold">✅ User Berhasil Dibuat!</div>
                        <div className="text-xs">
                            Username: <span className="font-medium">{formData.username}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            Role: {formData.role}
                        </div>
                    </div>,
                    { duration: 4000 }
                );
                setShowCreateDialog(false);
                setFormData({ username: '', password: '', role: 'user' });
                loadUsers();
            }
        } catch (error) {
            toast.error(
                <div className="flex flex-col gap-1">
                    <div className="font-semibold">❌ Gagal Membuat User</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {error.response?.data?.message || 'Terjadi kesalahan'}
                    </div>
                </div>,
                { duration: 5000 }
            );
        }
    };

    const handleUpdateRole = async () => {
        if (!selectedUser) return;

        try {
            const response = await usersAPI.update(selectedUser.id, { role: formData.role });
            if (response.data.success) {
                toast.success('Role user berhasil diperbarui');
                setShowEditDialog(false);
                loadUsers();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Gagal memperbarui role');
        }
    };

    const handleChangePassword = async () => {
        if (!selectedUser || !newPassword) {
            toast.error('Password baru harus diisi');
            return;
        }

        try {
            const response = await usersAPI.changePassword(selectedUser.id, newPassword);
            if (response.data.success) {
                toast.success('Password berhasil diubah');
                setShowPasswordDialog(false);
                setNewPassword('');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Gagal mengubah password');
        }
    };

    const handleDeleteUser = async () => {
        if (!deleteTarget) return;

        try {
            const response = await usersAPI.delete(deleteTarget.id);
            if (response.data.success) {
                toast.success('User berhasil dihapus');
                setDeleteTarget(null);
                loadUsers();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Gagal menghapus user');
        }
    };

    const openEditDialog = (user) => {
        setSelectedUser(user);
        setFormData({ ...formData, role: user.role });
        setShowEditDialog(true);
    };

    const openPasswordDialog = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setShowPasswordDialog(true);
    };

    if (loading) {
        return <LoadingSpinner text="Memuat data user..." />;
    }

    return (
        <div className="space-y-4">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                        <span>Manajemen</span>
                        <span>›</span>
                        <span style={{ color: STATUS.primary.text }} className="font-medium">Manajemen Pengguna</span>
                    </div>
                    <h2 className="text-base sm:text-lg font-semibold tracking-tight flex items-center gap-2">
                        <Users className="h-4 w-4" style={{ color: STATUS.primary.icon }} />
                        Manajemen Pengguna
                    </h2>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                        Kelola user dan hak akses sistem
                    </p>
                </div>
                <Button size="sm" onClick={() => setShowCreateDialog(true)}>
                    <UserPlus className="h-3.5 w-3.5 mr-1" />
                    Tambah User
                </Button>
            </div>

            {/* Users List */}
            <Card>
                <CardHeader>
                    <CardTitle>Daftar User</CardTitle>
                    <CardDescription>
                        Total {users.length} user terdaftar
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {users.map((user) => (
                            <div
                                key={user.id}
                                className="flex items-center justify-between p-3 border rounded-sm hover:bg-muted/30 transition-colors"
                            >
                                <div className="flex items-start gap-3">
                                    <div
                                        className="p-2 rounded-full mt-0.5"
                                        style={{
                                            background: user.role === 'admin' ? STATUS.primary.bg : STATUS.expired.bg,
                                            color: user.role === 'admin' ? STATUS.primary.icon : STATUS.expired.icon,
                                        }}
                                    >
                                        {user.role === 'admin' ? <Shield className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-xs font-semibold">{user.username}</h4>
                                            <Badge
                                                variant="outline"
                                                style={
                                                    user.role === 'admin'
                                                        ? { background: STATUS.primary.bg, borderColor: STATUS.primary.border, color: STATUS.primary.text }
                                                        : { background: STATUS.expired.bg, borderColor: STATUS.expired.border, color: STATUS.expired.text }
                                                }
                                            >
                                                {user.role === 'admin' ? 'Admin' : 'User'}
                                            </Badge>
                                            {user.id === currentUser?.id && (
                                                <Badge variant="outline">Anda</Badge>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            Dibuat: {user.created_at ? formatDate(user.created_at) : 'N/A'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => openPasswordDialog(user)}
                                        title="Ubah Password"
                                    >
                                        <Key className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => openEditDialog(user)}
                                        disabled={user.id === currentUser?.id}
                                        title="Edit Role"
                                    >
                                        <Edit2 className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 hover:text-destructive"
                                        onClick={() => setDeleteTarget(user)}
                                        disabled={user.id === currentUser?.id}
                                        title="Hapus User"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Create User Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="h-4 w-4" style={{ color: STATUS.primary.icon }} />
                            Tambah User Baru
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3 py-1">
                        <div className="space-y-1.5">
                            <Label htmlFor="new-username">Username</Label>
                            <Input
                                id="new-username"
                                type="text"
                                placeholder="Masukkan username"
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="new-password">Password</Label>
                            <Input
                                id="new-password"
                                type="password"
                                placeholder="Masukkan password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label>Role</Label>
                            <Select
                                value={formData.role}
                                onValueChange={(v) => setFormData({ ...formData, role: v })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="user">User (Akses Terbatas)</SelectItem>
                                    <SelectItem value="admin">Admin (Akses Penuh)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                            Batal
                        </Button>
                        <Button onClick={handleCreateUser}>
                            <Save className="h-3.5 w-3.5 mr-1" />
                            Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Role Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit2 className="h-4 w-4" style={{ color: STATUS.primary.icon }} />
                            Edit Role User
                        </DialogTitle>
                    </DialogHeader>

                    {selectedUser && (
                        <div className="space-y-3 py-1">
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground">Username</Label>
                                <p className="text-xs font-mono bg-muted p-2 rounded-sm">
                                    {selectedUser.username}
                                </p>
                            </div>

                            <div className="space-y-1.5">
                                <Label>Role</Label>
                                <Select
                                    value={formData.role}
                                    onValueChange={(v) => setFormData({ ...formData, role: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="user">User (Akses Terbatas)</SelectItem>
                                        <SelectItem value="admin">Admin (Akses Penuh)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                            Batal
                        </Button>
                        <Button onClick={handleUpdateRole}>
                            <Save className="h-3.5 w-3.5 mr-1" />
                            Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Change Password Dialog */}
            <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Key className="h-4 w-4" style={{ color: STATUS.primary.icon }} />
                            Ubah Password
                        </DialogTitle>
                    </DialogHeader>

                    {selectedUser && (
                        <div className="space-y-3 py-1">
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground">Username</Label>
                                <p className="text-xs font-mono bg-muted p-2 rounded-sm">
                                    {selectedUser.username}
                                </p>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="new-user-password">Password Baru</Label>
                                <Input
                                    id="new-user-password"
                                    type="password"
                                    placeholder="Masukkan password baru"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                            Batal
                        </Button>
                        <Button onClick={handleChangePassword}>
                            <Key className="h-3.5 w-3.5 mr-1" />
                            Ubah Password
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Konfirmasi Hapus */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus User?</AlertDialogTitle>
                        <AlertDialogDescription>
                            User <strong>{deleteTarget?.username}</strong> akan dihapus secara permanen dan tidak
                            dapat dikembalikan.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                            className="w-full sm:w-auto text-white"
                            style={{ background: STATUS.critical.icon }}
                            onClick={handleDeleteUser}
                        >
                            Hapus
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
